/* eslint react-hooks/exhaustive-deps: "warn" */

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { IconBadge } from 'ui/atoms/IconBadge/IconBadge';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DeliverySection.module.scss';

type Translate = (key: string) => string;

const getGroups = (t: Translate) => [
  {
    category: t('Query it directly'),
    items: [
      {
        icon: <IconBadge iconType="i-8" color="white" size="sm" />,
        name: 'REST API',
        description: t('Vet a supplier inside your onboarding flow; the full attributed record returned as JSON.'),
        ctas: [{ label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
      },
      {
        icon: <IconBadge iconType="i-22" color="white" size="sm" />,
        name: t('Dashboard'),
        description: t('Search, vet, and export supplier records from the browser — no integration required.'),
        ctas: [{ label: t('Start a free trial'), href: '/free-trial' }],
      },
    ],
  },
  {
    category: t('Ask in plain language'),
    items: [
      {
        icon: <IconBadge iconType="i-36" color="white" size="sm" />,
        name: 'Claude & ChatGPT',
        description: t(
          'Ask supplier-risk questions in plain language inside Claude or ChatGPT, with sources attached.',
        ),
        ctas: [
          { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
          { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
        ],
      },
      {
        icon: <IconBadge iconType="i-44" color="white" size="sm" />,
        name: t('Ask Regis'),
        description: t('Put supplier questions to Regis, our AI analyst, and get verified answers with sources.'),
        ctas: [{ label: t('Ask Regis'), href: '/regis' }],
      },
    ],
  },
  {
    category: t('Integrate at scale'),
    items: [
      {
        icon: <IconBadge iconType="i-79" color="white" size="sm" />,
        name: t('Bulk supplier feeds'),
        description: t(
          'Screen and refresh your whole supplier base on a schedule, delivered via SFTP or your warehouse.',
        ),
        ctas: [{ label: t('Request a data feed'), href: '/book-a-demo' }],
      },
      {
        icon: <IconBadge iconType="i-83" color="white" size="sm" />,
        name: t('Procurement & CRM'),
        description: t(
          'Enrich the supplier records in your procurement, ERP, or CRM system, keyed to open identifiers.',
        ),
        ctas: [{ label: t('See integrations'), href: '/crm' }],
      },
    ],
  },
];

export const DeliverySection = () => {
  const { t } = useInt();
  const groups = getGroups(t);

  return (
    <Section layout="centered" title={t('Fits the systems your risk team already runs')}>
      <div className={styles.grid}>
        {groups.map((group) => (
          <div key={group.category} className={styles.group}>
            <div className={styles.cat}>{group.category}</div>
            {group.items.map((item) => (
              <div key={item.name} className={styles.item}>
                <div className={styles.top}>
                  {item.icon}
                  <span className={styles.name}>{item.name}</span>
                </div>

                <p className={styles.desc}>{item.description}</p>

                <div className={styles.ctas}>
                  {item.ctas.map((cta) => (
                    <Button key={cta.label} type="dark-outline" size="lg" href={cta.href}>
                      {cta.label}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
};
